import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../Utils/toastContext";
import { debounce } from "lodash";
import { useSelector } from "react-redux";

export default function CertificateViewer({
  url,
  isOpen,
  onClose,
  certificateId,
  customerId
}) {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const roleName = useSelector((state) => state.user.roleName);

  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    if (!isOpen) return;

    const fetchCertificate = async () => {
      try {
        setLoading(true);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer YOUR_TOKEN_HERE`, // 🔥 important
          },
        });

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        setFileUrl(objectUrl);
      } catch (err) {
        console.error("Error loading certificate:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();

    return () => {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
    };
  }, [isOpen, url]);
  const downloadExcelReportHistoricalPerformance = async () => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/pdf",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(roleName=="agent"?`${BASE_URL}/api/v1/agent/customers/${customerId}/certificate/${certificateId}/download-pdf`:
        `${BASE_URL}/api/v1/customer/certificate/${certificateId}/download-pdf`,
        {
          method: "GET",
          headers,
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          // Optional: clear token and redirect or notify
          localStorage.removeItem("jwtToken");
          navigate("/login");
          // Example: redirect to login page
          // window.location.href = "/login";

          // Or throw a custom error
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));

        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.blob();
    } catch (error) {
      throw new Error(error.message || "Something went wrong");
    }
  };
  // const handleDownload = async () => {
  //   try {
  //     const token = localStorage.getItem("jwtToken");

  //     const response = await fetch(`/api/v1/customer/certificate/${certificateId}/download-pdf`, {
  //       method: "GET",
  //       headers: {
  //         ...(token && { Authorization: `Bearer ${token}` }),
  //       },
  //     });

  //     if (!response.ok) throw new Error("Failed to download PDF");

  //     const blob = await response.blob();

  //     // Extract filename from content-disposition header (optional)
  //     const disposition = response.headers.get("content-disposition");
  //     let filename = `certificate-${certificateId}.pdf`;
  //     if (disposition && disposition.includes("filename=")) {
  //       filename = disposition.split("filename=")[1].replace(/"/g, "");
  //     }

  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = filename;
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //     window.URL.revokeObjectURL(url);

  //   } catch (error) {
  //     console.error("Download error:", error);
  //   }
  // };
  const handleDownload = useCallback(
    debounce(async () => {
      try {
        const csvBlob = await downloadExcelReportHistoricalPerformance();
        const url = window.URL.createObjectURL(csvBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Certificate.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setTimeout(function () {
          setLoadingDownload(false); // Ensure `loading` is false first
        }, 100);
      }
    }, 1000), // Debounce time
    [], // Dependencies
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      {loadingDownload && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="w-full max-w-5xl h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 bg-gradient-to-r from-[#375AE7] to-purple-600 text-white">
          <h2 className="text-lg font-semibold">Certificate Preview</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg"
          >
            ✕
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-center px-5 py-3 border-b bg-gray-50">
          <button
            onClick={() => {
              setLoadingDownload(true);
              handleDownload();
            }}
            className="bg-[#375AE7] hover:bg-[#375AE7] items-center text-white px-4 py-2 rounded-lg text-sm"
          >
            ⬇ Download
          </button>
        </div>

        {/* Viewer */}
        <div className="flex-1 bg-gray-100 flex items-center justify-center">
          {loading ? (
            <div className="text-gray-500">Loading certificate...</div>
          ) : fileUrl ? (
            <iframe
              src={fileUrl}
              title="Certificate"
              className="w-full h-full"
            />
          ) : (
            <div className="text-red-500">Failed to load certificate</div>
          )}
        </div>
      </div>
    </div>
  );
}